import React, { useContext, useLayoutEffect, useRef, useState } from 'react'
import { Card } from 'antd'
import './AgentLeaderboard.css'
import { AppContext } from 'AppContext'

const getInitials = (name = '') =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

const colorPalette = [
  '#13806C',
  '#F1416C',
  '#3E97FF',
  '#FFC107',
  '#6F42C1',
  '#17A2B8',
]

const getColor = (index) => colorPalette[index % colorPalette.length]

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return 0

  const isNegative = timeStr.trim().startsWith('-')
  const cleanStr = timeStr.replace(/^-/, '').trim()

  if (cleanStr.includes('h')) {
    const parts = cleanStr.split('h')
    const hours = parseInt(parts[0]) || 0
    const minutesPart = parts[1]?.replace('m', '').trim() || '0'
    const minutes = parseInt(minutesPart) || 0
    const totalMinutes = hours * 60 + minutes
    return isNegative ? -totalMinutes : totalMinutes
  } else {
    const minutes = parseInt(cleanStr.replace('m', '').trim()) || 0
    return isNegative ? -minutes : minutes
  }
}

export default function AgentPerformanceLeaderboard({
  agentPerformanceLeaderboard = [],
}) {
  const {
    state: { isMobile },
  } = useContext(AppContext)

  const tableRef = useRef(null)
  const [tableHeight, setTableHeight] = useState(338)

  const enrichedData = [...agentPerformanceLeaderboard]
    .filter((agent) => agent && agent.averageTime)
    .sort((a, b) => {
      const timeA = parseTimeToMinutes(a.averageTime)
      const timeB = parseTimeToMinutes(b.averageTime)
      if (isNaN(timeA) || timeA < 0) return 1
      if (isNaN(timeB) || timeB < 0) return -1
      return timeA - timeB
    })
    .map((agent, index) => ({
      ...agent,
      rank: index + 1,
      badge: getInitials(agent.name),
      badgeColor: getColor(index),
    }))

  const topThree = enrichedData.slice(0, 3)

  // Order: 2nd, 1st, 3rd
  const podiumOrder =
    topThree.length >= 3
      ? [topThree[1], topThree[0], topThree[2]]
      : topThree

  useLayoutEffect(() => {
    if (tableRef.current) {
      const height = tableRef.current.offsetHeight
      setTableHeight(Math.max(height, 338))
    }
  }, [agentPerformanceLeaderboard])

  const minTime =
    topThree.length > 0
      ? Math.min(...topThree.map((a) => parseTimeToMinutes(a.averageTime)))
      : 0

  const getPodiumHeight = (value) => {
    const minHeight = 120
    const maxHeight = 200
    const valueInMin = parseTimeToMinutes(value)

    if (valueInMin <= 0 || minTime <= 0) {
      return `${minHeight}px`
    }

    const relativeHeight =
      (minTime / valueInMin) * (maxHeight - minHeight) + minHeight

    return `${relativeHeight}px`
  }

  return (
    <Card className='leaderboard-card'>
      <div className='chart-header'>
        <div className='chart-title'>
          <span className='ticket-count'>
            Agent Performance Leaderboard
          </span>
        </div>
      </div>

      <div className='leaderboard-content'style={{
        flexDirection: isMobile && 'row'
      }}>
        {/* Podium Section */}
        <div className='podium-section'>
          <div
            className='podium-container'
            style={{ minHeight: `${tableHeight}px` }}
          >
            <div className='podium-base'></div>

            {podiumOrder.map((agent) => {
              const isFirst = agent.rank === 1
              const isSecond = agent.rank === 2

              const avatarSize = isFirst
                ? 65
                : isSecond
                ? 55
                : 45

              return (
                <div
                  key={agent.rank}
                  className={`podium-item ${
                    agent.rank === 1
                      ? 'first-place'
                      : agent.rank === 2
                      ? 'second-place'
                      : 'third-place'
                  }`}
                  style={{
                    height: getPodiumHeight(agent.averageTime),
                  }}
                >
                  {/* Avatar INSIDE podium-item */}
                  <div
                    className='podium-user-avatar'
                    style={{
                      width: avatarSize,
                      height: avatarSize,
                      top: `-${avatarSize * 1.2}px`,
                    }}
                  >
                    {agent.badge}
                  </div>

                  <div
                    className='badge'
                    style={{ backgroundColor: agent.badgeColor }}
                  >
                    {agent.name}
                  </div>

                  <div className='rating-display'>
                    ⏱ {agent.averageTime}
                  </div>

                  <div className='podium-rank'>{agent.rank}</div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Table Section */}
        <div className='table-section' ref={tableRef}>
          <table>
            <thead className='table-header'>
              <tr>
                <th>Sr</th>
                <th>Agent</th>
                <th>Avg. Time</th>
              </tr>
            </thead>
            <tbody>
              {enrichedData.map((agent) => (
                <tr key={agent.id || agent.name}>
                  <td>{agent.rank}</td>
                  <td>{agent.name}</td>
                  <td>
                    <span className='time-display'>
                      ⏱ {agent.averageTime}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  )
}